"use client";

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ButtonGroup from "@/components/atoms/button-group/button-group";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import withApplication from "@/components/with-application";
import {
  RenewalNextYearRenew,
  RenewalPage,
  ReviewAutoRenewal,
} from "@/config/app-constant";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { formatDate as canonicalFormatDate } from "@/libs/i18n/format-date";
import { useRenewalReviewStateContext } from "@/providers/renewal-review-state-provider";
import { RootState } from "@/store/root-reducer";
import { setQuote } from "@/store/slices/quote/quote.slice";
import { PaymentDetails } from "@/types/payment-details-types";
import { AutoRenewalProps } from "@/types/review-types";

import Style from "./review-section.module.css";

function PolicyRenewal({
  policyRenewalTitle,
  policyRenewalTopContent,
  autoRenewBtnLabel,
  autoRenewalTitle,
  autoRenewSubTitlePartOne,
  autoRenewSubTitlePartTwo,
  autoRenewalContent,
  autoRenewalConfirmBtnTitle,
  autoRenewalConfirmBtnContent,
  autoRenewalCancelBtnTitle,
  autoRenewalCancelBtnContent,
  autoRenewalContentBottom,
  application,
}: Readonly<AutoRenewalProps & { application?: string }>) {
  const dispatch = useDispatch();
  const quote = useSelector((state: RootState) => state.quote);
  const isQuoteDisabled = useQuoteDisabled();
  const [isExpand, setIsExpand] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(
    Number(
      application === RenewalPage
        ? quote?.attributes?.paymentDetails?.optOutNextYearAutoRenewal
        : quote?.attributes?.paymentDetails?.optOutAutoRenewal
    )
  );

  const { setOptOutAutoRenewal } = useRenewalReviewStateContext();

  useEffect(() => {
    if (application === RenewalPage) {
      setOptOutAutoRenewal(selectedAnswer === 1);
    }
  }, [application, selectedAnswer, setOptOutAutoRenewal]);

  const changeRenewalStatus = (status: boolean) => {
    dispatch(
      setQuote({
        ...quote,
        attributes: {
          ...quote.attributes,
          paymentDetails: {
            ...(quote.attributes?.paymentDetails as PaymentDetails),
            optOutAutoRenewal: status,
          },
        },
      })
    );
  };

  const buttonClick = (e: number) => {
    const shouldRenew = e === 1;

    setSelectedAnswer(e);

    if (application === RenewalPage) {
      setOptOutAutoRenewal(shouldRenew);
      return;
    }

    if (quote?.attributes?.paymentDetails?.optOutAutoRenewal !== shouldRenew) {
      changeRenewalStatus(shouldRenew);
    }
  };

  const formatDate = useCallback(
    (dateString: string | undefined) =>
      canonicalFormatDate(dateString, "dd/MM/yyyy"),
    []
  );

  return (
    <main className="PolicyRenewal" data-testid="policyRenewal-testid">
      <div className=" flex flex-col gap-spacing-xs ">
        <h1
          className={` font-header-font  text-[23px] ${
            application === RenewalPage
              ? "md:text-[31px] md:leading-custom-size-xl"
              : "md:text-[25px] md:leading-[27.5px]"
          }
           leading-[27.6px] text-text-title  md:text-left `}
        >
          {policyRenewalTitle}
        </h1>

        <div className={`${Style.list_block} leading-6 text-2xs`}>
          <RichText
            richText={policyRenewalTopContent}
            className="flex flex-col gap-spacing-xs"
          />
        </div>
      </div>
      <div className="flex mt-spacing-s ">
        <LabelButtonWrapper
          data-testid="policy-renewal-expand-button"
          id="policy-renewal-expand-button"
          isDisabled={isQuoteDisabled}
          label={autoRenewBtnLabel}
          hierarchy="tertiary"
          className={`${Style.expand_btn} !p-0 font-semibold`}
          type="button"
          onClick={() => setIsExpand(true)}
        />
      </div>
      {isExpand && (
        <div className="mt-spacing-l">
          {autoRenewalTitle && (
            <h1
              className=" font-header-font md:text-[25px] text-[23px]
          md:leading-[27.5px] leading-[27.6px] text-text-title  md:text-left mb-spacing-xs"
            >
              {autoRenewalTitle}
            </h1>
          )}
          <div className="leading-6 text-2xs flex flex-col gap-spacing-xs">
            <p>
              {`${autoRenewSubTitlePartOne} ${formatDate(
                quote?.attributes?.tripDetails?.policyEndDate
              )}.`}
            </p>
            <p> {autoRenewSubTitlePartTwo}</p>
          </div>
          <div
            className={`${Style.list_block} leading-6 text-2xs mt-spacing-s`}
          >
            <RichText
              richText={autoRenewalContent}
              className={`${
                application === RenewalPage
                  ? "[&_li]:!mb-spacing-xs [&_li]:!pl-spacing-xs [&_li:last-child]:!mb-0"
                  : ""
              }`}
            />
          </div>

          <div
            className={`justify-center ${
              application === RenewalPage
                ? "md:mt-spacing-m mt-spacing-l"
                : "mt-spacing-l "
            }`}
          >
            <ButtonGroup
              selectedAnswer={selectedAnswer}
              answerArray={[
                {
                  id: 0,
                  label: autoRenewalConfirmBtnTitle,
                  description: autoRenewalConfirmBtnContent,
                },
                {
                  id: 1,
                  label: autoRenewalCancelBtnTitle,
                  description: autoRenewalCancelBtnContent,
                },
              ]}
              location={
                application === RenewalPage
                  ? RenewalNextYearRenew
                  : ReviewAutoRenewal
              }
              setSelectedAnswer={buttonClick}
              type="policy-renewal"
            />
          </div>
          <div className="mt-spacing-l leading-6 text-2xs">
            {autoRenewalContentBottom}
          </div>
        </div>
      )}
    </main>
  );
}

export default withApplication(PolicyRenewal);
